import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSWRConfig } from "swr";

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

import { ThinkingIndicator } from "./ScoutMessages";
import ScoutThread, { getVisibleMessages } from "./ScoutThread";
import ScoutThreadHeaderToggle from "./ScoutThreadHeaderToggle";
import { ScoutCardContext } from "./ScoutCardContext";
import { getSurfaceOpener } from "./scoutOpeners";
import { cardRegistry } from "./cardRegistry";
import ConversationHistoryPanel from "./ConversationHistoryPanel";
import { dispatchActionHandler } from "./cards/actionHandlers";

import ScoutMark from "../../atoms/icons/ScoutMark";
import { useSupportSlider } from "../../atoms/buttons/TopBarSupportIcon";
import CommunicationCenter from "../../modules/CommunicationCenter";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../helpers/accountHelpers";
import { generateTempId } from "../../../helpers/otherHelpers";
import {
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useMutateCachedCollection
} from "../../../helpers/firebase";
import { analyticsTrack } from "../../../helpers/analytics";
import { useShareCustomerYear } from "../../../helpers/components/vendors";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";

const turnsFromConversation = (conversation) =>
  (conversation?.turns || []).map((turn) => ({
    role: turn.role,
    content: turn.content,
    sources: turn.sources || [],
    cards: turn.cards || [],
    surface: turn.surface,
    activeTab: turn.activeTab
  }));

const OpenerCard = ({ card }) => {
  const Component = cardRegistry[card.type];
  if (!Component) {
    console.warn(
      `[ExplorerScoutCanvas] No renderer registered for card type "${card.type}"`
    );
    return (
      <Typography variant="caption" color="textSecondary">
        {`Scout sent a card this version can't display (${card.type}).`}
      </Typography>
    );
  }
  return <Component {...(card.props || {})} />;
};

const handleChipActivation = ({
  chip,
  onChipClick,
  onClientAction,
  appendSyntheticTurn,
  recordInvokedAction,
  cardActionDeps
}) => {
  const action = chip.clientAction;
  const surfaceHandler = action && onClientAction?.[action.type];
  if (surfaceHandler) {
    const result = surfaceHandler(action);
    if (action.type) {
      recordInvokedAction?.(action.type);
    }
    if (result && (result.userText || result.assistantText)) {
      appendSyntheticTurn(result);
    }
    return;
  }
  const dispatched =
    action?.type &&
    dispatchActionHandler({
      actionType: action.type,
      payload: action.payload,
      deps: cardActionDeps
    });
  if (dispatched) {
    recordInvokedAction?.(action.type);
    return;
  }
  if (chip.prompt) {
    onChipClick(chip.prompt);
  }
};

const ChipRail = ({
  chips,
  onChipClick,
  onClientAction,
  appendSyntheticTurn,
  recordInvokedAction,
  cardActionDeps,
  keyPrefix
}) => (
  <Box display="flex" flexWrap="wrap" gap={1}>
    {chips.map((chip, idx) => (
      <Chip
        key={`${keyPrefix}-chip-${idx}`}
        label={chip.label}
        variant="outlined"
        size="small"
        color="primary"
        onClick={() =>
          handleChipActivation({
            chip,
            onChipClick,
            onClientAction,
            appendSyntheticTurn,
            recordInvokedAction,
            cardActionDeps
          })
        }
      />
    ))}
  </Box>
);

const Opener = ({
  opener,
  onChipClick,
  onClientAction,
  appendSyntheticTurn,
  recordInvokedAction,
  cardActionDeps,
  palette
}) => (
  <Box display="flex" flexDirection="column" gap={2}>
    <Box
      px={2}
      py={1}
      style={{
        backgroundColor: palette.backgroundGray.dark,
        borderRadius: "8px",
        maxWidth: "85%"
      }}
    >
      <Typography variant="body2" color="textPrimary">
        {opener.greeting}
      </Typography>
    </Box>
    <ChipRail
      chips={opener.suggestedChips}
      onChipClick={onChipClick}
      onClientAction={onClientAction}
      appendSyntheticTurn={appendSyntheticTurn}
      recordInvokedAction={recordInvokedAction}
      cardActionDeps={cardActionDeps}
      keyPrefix="opener"
    />
    {(opener.cards || []).map((card, idx) => (
      <Box key={`opener-card-${idx}`} style={{ maxWidth: "85%" }}>
        <OpenerCard card={card} />
      </Box>
    ))}
  </Box>
);

const IDLE_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;

const SURVEY_CONTEXTS = new Set(["emissions-survey", "bom-survey"]);

const findActiveConversation = (conversations) =>
  (conversations || []).find(
    (c) =>
      !c.archived &&
      !SURVEY_CONTEXTS.has(c.context) &&
      (c.scope === "dashboard" || !c.scope)
  );

const isConversationStale = (conversation) =>
  Boolean(
    conversation?.updatedAt &&
    Date.now() - conversation.updatedAt > IDLE_THRESHOLD_MS
  );

const ExplorerScoutCanvas = ({
  surface,
  surfaceContext,
  onClientAction,
  headerCenter,
  variant = "canvas",
  onCollapse
}) => {
  const { palette } = useTheme();
  const isRail = variant === "rail";
  const { openSupport, supportSliderJsx } = useSupportSlider();

  const [conversations, conversationsLoading] = useCachedDisplayData(
    "agent-conversations"
  );
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const mutateCachedCollection = useMutateCachedCollection();
  const { mutate } = useSWRConfig();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [retryNotice, setRetryNotice] = useState(null);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [isExpertMeetingOpen, setIsExpertMeetingOpen] = useState(false);
  const [invokedActionTypes, setInvokedActionTypes] = useState(() => new Set());
  const [viewMode, setViewMode] = useState("filtered");
  const bottomRef = useRef(null);
  const replyInputRef = useRef(null);

  const activeTab = surfaceContext?.activeTab;

  const handleViewModeToggle = () => {
    const nextMode = viewMode === "filtered" ? "all" : "filtered";
    analyticsTrack("scout_thread_view_mode_toggled", {
      newMode: nextMode,
      surface,
      activeTab
    });
    setViewMode(nextMode);
  };

  const recordInvokedAction = useCallback(
    (type) =>
      setInvokedActionTypes((prev) =>
        prev.has(type) ? prev : new Set([...prev, type])
      ),
    []
  );

  const openExpertMeeting = useCallback(() => setIsExpertMeetingOpen(true), []);

  useEffect(() => {
    if (conversationsLoading) return;
    if (conversationId) return;
    const existing = findActiveConversation(conversations);
    if (!existing) {
      setConversationId(String(generateTempId()));
      return;
    }
    if (isConversationStale(existing)) {
      Promise.resolve(
        updateCollectionDoc("agent-conversations", existing.id, {
          archived: true
        })
      ).catch((err) => {
        console.error(
          `[ExplorerScoutCanvas] Failed to auto-archive stale conversation ${existing.id}:`,
          err
        );
      });
      setConversationId(String(generateTempId()));
      return;
    }
    setConversationId(existing.id);
  }, [
    conversations,
    conversationsLoading,
    conversationId,
    updateCollectionDoc
  ]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    if (isLoading) {
      return;
    }
    const conv = (conversations || []).find((c) => c.id === conversationId);
    if (!conv) {
      return;
    }
    const persisted = turnsFromConversation(conv);
    setMessages((prev) => {
      const localOnly = prev.filter((message) => message.synthetic);
      return [...persisted, ...localOnly];
    });
  }, [conversationId, conversations, isLoading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      replyInputRef.current?.focus();
    }
  }, [isLoading, messages.length]);

  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");
  const { activateSnackbar } = useExplorerLayoutContext() || {};

  const shareCustomerYear = useShareCustomerYear();
  const { canShare } = usePermissions();

  const acceptSupplierShare = useCallback(
    async ({ relationshipId, year }) => {
      if (!canShare) {
        activateSnackbar?.({
          severity: "error",
          message: "You don't have permission to accept supplier shares."
        });
        return;
      }
      if (!relationshipId || year === undefined || year === null) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't accept — missing relationship or year."
        });
        return;
      }
      const numericYear = Number(year);
      const relationship = (vendorRelationships || []).find(
        ({ id }) => id === relationshipId
      );
      if (!relationship) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't find that supplier. Refresh and try again?"
        });
        return;
      }
      const existingSurveyData = relationship.surveyData || [];
      const matchingEntry = existingSurveyData.find(
        ({ year: y }) => Number(y) === numericYear
      );
      const isAcceptable =
        matchingEntry?.status === "pending" ||
        matchingEntry?.status === "unconfirmed";
      if (!isAcceptable) {
        activateSnackbar?.({
          severity: "info",
          message: `No pending ${numericYear} share to accept from this supplier.`
        });
        return;
      }
      const vendorLabel = relationship.vendorName || "this supplier";
      analyticsTrack("scout_supplier_share_accepted", {
        relationshipId,
        year: numericYear
      });
      try {
        const response = await fetchOurApi({
          path: "/vendors/accept-vendor-survey-data",
          method: "POST",
          data: { relationshipId, year: numericYear },
          callback: (res) => res
        });
        if (!response?.success) {
          activateSnackbar?.({
            severity: "error",
            message: `Couldn't accept ${vendorLabel}'s share. Try again?`
          });
          return;
        }
        // useCachedDisplayData is an SWR fetch, not a live Firestore listener --
        // without this, vendorRelationships keeps serving the pre-request
        // pending/unconfirmed status until an unrelated revalidation happens,
        // so the card would still look acceptable and could be accepted again.
        await mutateCachedCollection("vendor-relationships");
        activateSnackbar?.({
          severity: "success",
          message: `Accepted ${vendorLabel}'s ${numericYear} share. Their emissions data is now part of your footprint.`
        });
      } catch (err) {
        console.error("[acceptSupplierShare] backend call threw", err);
        activateSnackbar?.({
          severity: "error",
          message: `Couldn't accept ${vendorLabel}'s share. Check your connection and try again.`
        });
      }
    },
    [canShare, vendorRelationships, activateSnackbar, mutateCachedCollection]
  );

  const cardContextValue = useMemo(
    () => ({
      surface,
      openSupport,
      openExpertMeeting,
      shareCustomerYear,
      acceptSupplierShare
    }),
    [
      surface,
      openSupport,
      openExpertMeeting,
      shareCustomerYear,
      acceptSupplierShare
    ]
  );

  const cardActionDeps = useMemo(
    () => ({
      openSupport,
      openExpertMeeting,
      shareCustomerYear,
      acceptSupplierShare
    }),
    [openSupport, openExpertMeeting, shareCustomerYear, acceptSupplierShare]
  );

  const opener = useMemo(
    () => getSurfaceOpener(surface, surfaceContext),
    [surface, surfaceContext]
  );

  const visibleMessages = useMemo(
    () => getVisibleMessages(messages, activeTab, viewMode),
    [messages, activeTab, viewMode]
  );

  const shouldShowOpener = visibleMessages.length === 0 && !isLoading;

  const askedPrompts = useMemo(
    () =>
      new Set(
        messages
          .filter(({ role }) => role === "user")
          .map(({ content }) => (content || "").trim())
      ),
    [messages]
  );

  const unusedChips = useMemo(
    () =>
      (opener.suggestedChips || []).filter((chip) => {
        const promptUsed = chip.prompt && askedPrompts.has(chip.prompt.trim());
        const actionUsed =
          chip.clientAction?.type &&
          invokedActionTypes.has(chip.clientAction.type);
        return !promptUsed && !actionUsed;
      }),
    [opener.suggestedChips, askedPrompts, invokedActionTypes]
  );

  const sendChat = (companyId, message) =>
    fetchOurApi({
      path: "/agents/chat",
      method: "POST",
      data: { companyId, conversationId, message, surface, surfaceContext },
      callback: (res) => res
    });

  const sendMessage = async (messageOverride) => {
    const trimmed = (messageOverride || input).trim();
    if (!trimmed || isLoading || !conversationId) {
      return;
    }

    const sendActiveTab = activeTab;
    const companyId = getAccountId();
    const optimisticLocalId = String(generateTempId());
    const optimisticUserMessage = {
      role: "user",
      content: trimmed,
      _localId: optimisticLocalId,
      activeTab: sendActiveTab
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);
    setInput("");
    setIsLoading(true);
    setRetryNotice(null);

    const rollbackOptimisticUserMessage = () =>
      setMessages((prev) =>
        prev.filter((message) => message._localId !== optimisticLocalId)
      );

    const handleAssistantResponse = (response) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.response || "No response received.",
          sources: response.sources || [],
          cards: response.cards || [],
          activeTab: sendActiveTab
        }
      ]);
    };

    try {
      const first = await sendChat(companyId, trimmed);
      const needsRetry = first?.code === "turn_seq_conflict";
      const final = needsRetry ? await sendChat(companyId, trimmed) : first;
      const isConflict = final?.code === "turn_seq_conflict";
      const isError = !isConflict && final?.error === true;
      const isSuccess = final && !isConflict && !isError;
      if (isConflict) {
        console.error(
          "[ExplorerScoutCanvas] turn_seq_conflict persisted after retry",
          { conversationId, companyId }
        );
        rollbackOptimisticUserMessage();
        setRetryNotice("Scout had trouble sending that — try again.");
      }
      if (isError) {
        console.error("[ExplorerScoutCanvas] backend returned error response", {
          conversationId,
          companyId,
          code: final.code,
          response: final.response
        });
        rollbackOptimisticUserMessage();
        setRetryNotice(
          final.response ||
            "Something went wrong processing your request. Please try again."
        );
      }
      if (isSuccess) {
        handleAssistantResponse(final);
        await mutate("agent-conversations");
      }
    } catch (err) {
      console.error("[ExplorerScoutCanvas] sendChat threw", {
        conversationId,
        companyId,
        err
      });
      rollbackOptimisticUserMessage();
      setRetryNotice(
        "Something went wrong processing your request. Please try again."
      );
    }

    setIsLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleChipClick = (prompt) => sendMessage(prompt);

  const appendSyntheticTurn = ({ userText, assistantText, cards }) => {
    const userTurn = userText
      ? [{ role: "user", content: userText, synthetic: true, activeTab }]
      : [];
    const hasAssistantTurn =
      Boolean(assistantText) || (Array.isArray(cards) && cards.length > 0);
    const assistantTurn = hasAssistantTurn
      ? [
          {
            role: "assistant",
            content: assistantText || "",
            sources: [],
            cards: cards || [],
            synthetic: true,
            activeTab
          }
        ]
      : [];
    setMessages((prev) => [...prev, ...userTurn, ...assistantTurn]);
  };

  const handleStartFresh = () => {
    if (!conversationId) return;
    const isPersisted = (conversations || []).some(
      (c) => c.id === conversationId
    );
    if (isPersisted) {
      updateCollectionDoc("agent-conversations", conversationId, {
        archived: true
      });
    }
    setConversationId(String(generateTempId()));
    setMessages([]);
    setInput("");
    setRetryNotice(null);
    setHistoryAnchorEl(null);
    setInvokedActionTypes(new Set());
  };

  const handleSelectConversation = (id) => {
    setConversationId(id);
    setInput("");
    setRetryNotice(null);
    setHistoryAnchorEl(null);
    setInvokedActionTypes(new Set());
  };

  const openHistory = (event) => setHistoryAnchorEl(event.currentTarget);
  const closeHistory = () => setHistoryAnchorEl(null);

  const canStartFresh = messages.length > 0;

  return (
    <ScoutCardContext.Provider value={cardContextValue}>
      {supportSliderJsx}
      {isExpertMeetingOpen && (
        <CommunicationCenter
          initialTab="sales"
          isSlideOpen={isExpertMeetingOpen}
          setIsSlideOpen={setIsExpertMeetingOpen}
        />
      )}
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          width: "100%",
          minWidth: 0,
          minHeight: isRail
            ? "100%"
            : { xs: "calc(100vh - 170px)", md: "calc(100vh - 104px)" },
          ...(isRail ? { height: "100%" } : {}),
          gap: 2
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: isRail ? 0 : { xs: 104, md: 48 },
            zIndex: 1050,
            bgcolor: palette.backgroundGray.light,
            borderBottom: `1px solid ${palette.backgroundGray.main}`,
            display: "grid",
            gridTemplateColumns: headerCenter
              ? "auto minmax(0, 1fr) auto"
              : "auto auto",
            alignItems: "center",
            columnGap: 1,
            py: 1,
            px: isRail ? 1.5 : 0
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ScoutMark size={isRail ? 28 : 40} />
            {isRail && (
              <Typography variant="subtitle2" color="textPrimary">
                Scout
              </Typography>
            )}
          </Box>
          {headerCenter && (
            <Box
              sx={{ minWidth: 0, display: "flex", justifyContent: "center" }}
            >
              {headerCenter}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onCollapse && (
              <Tooltip title="Collapse Scout">
                <IconButton onClick={onCollapse}>
                  <KeyboardDoubleArrowRightIcon />
                </IconButton>
              </Tooltip>
            )}
            {activeTab && (
              <ScoutThreadHeaderToggle
                viewMode={viewMode}
                onToggle={handleViewModeToggle}
              />
            )}
            <Tooltip title="Conversation history">
              <IconButton onClick={openHistory}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            {canStartFresh && (
              <Tooltip title="Start a fresh conversation">
                <IconButton onClick={handleStartFresh}>
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Popover
          open={Boolean(historyAnchorEl)}
          anchorEl={historyAnchorEl}
          onClose={closeHistory}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box
            p={2}
            style={{ width: "360px", maxHeight: "480px", overflowY: "auto" }}
          >
            <ConversationHistoryPanel
              conversations={conversations || []}
              conversationsLoading={conversationsLoading}
              selectedId={conversationId}
              onSelect={handleSelectConversation}
              onNew={handleStartFresh}
            />
          </Box>
        </Popover>
        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          gap={2}
          px={isRail ? 1.5 : 0}
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            minWidth: 0
          }}
        >
          {shouldShowOpener && (
            <Opener
              opener={opener}
              onChipClick={handleChipClick}
              onClientAction={onClientAction}
              appendSyntheticTurn={appendSyntheticTurn}
              recordInvokedAction={recordInvokedAction}
              cardActionDeps={cardActionDeps}
              palette={palette}
            />
          )}
          <ScoutThread
            messages={messages}
            activeTab={activeTab}
            viewMode={viewMode}
            visibleMessages={visibleMessages}
          />
          {isLoading && <ThinkingIndicator />}
          {visibleMessages.length > 0 &&
            !isLoading &&
            unusedChips.length > 0 && (
              <ChipRail
                chips={unusedChips}
                onChipClick={handleChipClick}
                onClientAction={onClientAction}
                appendSyntheticTurn={appendSyntheticTurn}
                recordInvokedAction={recordInvokedAction}
                cardActionDeps={cardActionDeps}
                keyPrefix="persistent"
              />
            )}
          {retryNotice && (
            <Typography variant="caption" color="error">
              {retryNotice}
            </Typography>
          )}
          <div ref={bottomRef} />
        </Box>
        <Box
          display="flex"
          alignItems="flex-end"
          gap={1}
          px={isRail ? 1.5 : 0}
          pb={isRail ? 1.5 : 0}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ask Scout anything"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            multiline
            maxRows={4}
            disabled={isLoading}
            inputRef={replyInputRef}
          />
          <IconButton
            color="primary"
            onClick={() => sendMessage()}
            disabled={isLoading || !input.trim()}
          >
            {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </ScoutCardContext.Provider>
  );
};

export default ExplorerScoutCanvas;
